<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import subtotal from './../subtotal.vue';
import { getServices } from '@/api/api';
import { useBookingDataStore } from '@/stores/bookingData';
import { mapItemToBookingItem, buildNoonBookingItem } from '@/services/price';

const bookingData = useBookingDataStore();

const services = ref(Array());
const amount = ref<Record<string, number>>({});
const selectedDiner = ref('dinerlatable');
const selectedDisposition = ref('dinerlasalletables');

const isLasalle = computed(() => bookingData.venue === 'lasalle');
const isSerre   = computed(() => bookingData.venue === 'serre');

function getService(id: string) {
  return services.value.find((s: any) => s.Id === id);
}

const dinerMinValue = 2;
const dinerMaxValue = computed(() => {
  if (isSerre.value) {
    const s = getService('dinerlaserre');
    return s ? parseInt(s.Maximum) || 10 : 10;
  }
  const s = getService('diner');
  return s ? parseInt(s.Maximum) || 50 : 50;
});

onMounted(async () => {
  if (!bookingData.date) throw new Error('no date set');
  const s = await getServices(bookingData.date);
  if (s?.success && s.data) {
    services.value = s.data;
    services.value.forEach((o: any) => { amount.value[o.Id] = 0; });

    const noonSvc = getService('noon');
    if (noonSvc) amount.value['noon'] = dinerMinValue;

    // Restore from saved booking if returning to this step
    if (Array.isArray(bookingData.services)) {
      bookingData.services.forEach((saved: any) => {
        if (saved.id === 'noon' && saved.quantity > 0) {
          amount.value['noon'] = saved.quantity;
          if (isLasalle.value && saved.options?.[0]?.id === 'dinerlasalle') {
            selectedDiner.value = 'dinerlasalle';
          }
          if (saved.options?.[1]?.id) {
            selectedDisposition.value = saved.options[1].id;
          }
        }
      });
    }
  }
});

watch(() => amount.value['noon'], () => {
  const qty = amount.value['noon'] ?? 0;
  if (isLasalle.value) {
    if (qty > 6) selectedDiner.value = 'dinerlasalle';
    if (qty < 6) selectedDiner.value = 'dinerlatable';
    if (qty < 7 || qty > 18) selectedDisposition.value = 'dinerlasalletables';
  }
  updateNoonBooking();
});

function selectDiner(type: string) {
  selectedDiner.value = type;
  updateNoonBooking();
}

function selectDisposition(type: string) {
  selectedDisposition.value = type;
  updateNoonBooking();
}

function updateNoonBooking() {
  const noonSvc = getService('noon');
  if (!noonSvc) return;
  const qty = amount.value['noon'] ?? 0;
  if (qty === 0) return;

  let options: any[] = [];
  if (isLasalle.value) {
    const dinerOpt = mapItemToBookingItem(getService(selectedDiner.value), 1);
    const dispOpt  = mapItemToBookingItem(getService(selectedDisposition.value), 1);
    options = [dinerOpt, dispOpt];
  } else if (isSerre.value) {
    const serreOpt = mapItemToBookingItem(getService('dinerlaserre'), 1);
    const noDisp   = { id: 'none', activated: false, quantity: 0, packagePrice: '0', unitPrice: '0', options: [], meta: {} };
    options = [serreOpt, noDisp];
  }

  // No room resolved (venue is neither lasalle nor serre) — nothing to price.
  // Don't push a half-built service; this keeps the pre-helper fail-soft
  // behaviour for an out-of-range venue instead of throwing in the watch.
  if (options.length === 0) return;

  const booking = buildNoonBookingItem(noonSvc, qty, options);
  bookingData.updateService(booking);
  // Refresh the menu-step gate (step.allowMenu): nextStep() consults it, and
  // only the normal type step's template used to keep it in sync — without
  // this, the noon flow skips from Type straight to Options.
  bookingData.canGoMenu();
}

function canGoNext() {
  return (amount.value['noon'] ?? 0) > 0;
}
</script>

<template>
  <div class="bpa-front-tabs--panel-body __bpa-is-active eauget-booking-step-type" data-testid="step-type-noon-panel">
    <div class="bpa-front-default-card">
      <div class="bpa-front-dc--body">
        <div v-if="getService('noon')" class="bpa-front-module-container bpa-front-module--service" data-testid="noon-service-selection">
          <div class="bpa-front-module-heading">Votre réservation</div>
          <div class="bpa-front-module--service-items-row">
            <card-item
              :title="getService('noon').Titre"
              :description="getService('noon').Description"
              priceLabel="Découvrez l'expérience"
              :price="getService('noon').Prix"
              :pictureUrl="getService('noon').Photo"
              v-model="amount['noon']"
              :isSelected="true"
              :hasQuantity="true"
              quantityLabel="Personnes"
              :minValue="dinerMinValue"
              :maxValue="dinerMaxValue"
            />
          </div>

          <!-- Venue display for La Serre (locked by the invite): rendered
               selected and full-colour — not greyed out — with no click
               handler, so tapping it changes nothing. The single possible
               venue is a fact of the invite, not a disabled choice. -->
          <div v-if="isSerre" class="bpa-front-module-heading">Lieu</div>
          <div v-if="isSerre" class="bpa-front-module--service-items-row">
            <card-item
              v-if="getService('dinerlaserre')"
              :title="getService('dinerlaserre').Titre"
              :description="getService('dinerlaserre').Description"
              :price="getService('dinerlaserre').Prix"
              :pictureUrl="getService('dinerlaserre').Photo"
              :isSelected="true"
              :hasQuantity="false"
              v-model="amount['noon']"
            />
          </div>

          <!-- La Salle sub-picker (free choice by headcount) -->
          <span v-if="isLasalle && amount['noon'] > 0">
            <div class="bpa-front-module-heading">Le service</div>
            <div class="bpa-front-module--service-items-row">
              <card-item
                :title="getService('dinerlatable').Titre"
                :description="getService('dinerlatable').Description"
                :price="getService('dinerlatable').Prix"
                :disabled="amount['noon'] > 6"
                :pictureUrl="getService('dinerlatable').Photo"
                :isSelected="selectedDiner === 'dinerlatable'"
                :hasQuantity="false"
                v-model="amount['noon']"
                @click="selectDiner('dinerlatable')"
              />
              <card-item
                :title="getService('dinerlasalle').Titre"
                :description="getService('dinerlasalle').Description"
                :price="getService('dinerlasalle').Prix"
                :disabled="amount['noon'] < 4"
                :pictureUrl="getService('dinerlasalle').Photo"
                :isSelected="selectedDiner === 'dinerlasalle'"
                :hasQuantity="false"
                v-model="amount['noon']"
                @click="selectDiner('dinerlasalle')"
              />
            </div>
          </span>

          <!-- Disposition picker (lasalle venue, dinerlasalle selected) -->
          <span v-if="isLasalle && selectedDiner === 'dinerlasalle'">
            <div class="bpa-front-module-heading">Disposition des tables</div>
            <div class="bpa-front-module--service-items-row">
              <card-item
                :title="getService('dinerlasalletables').Titre"
                :description="getService('dinerlasalletables').Description"
                :disabled="amount['noon'] < 6"
                :pictureUrl="getService('dinerlasalletables').Photo"
                :isSelected="selectedDisposition === 'dinerlasalletables'"
                :hasQuantity="false"
                v-model="amount['noon']"
                @click="selectDisposition('dinerlasalletables')"
              />
              <card-item
                :title="getService('dinerlasalleovale').Titre"
                :description="getService('dinerlasalleovale').Description"
                :disabled="amount['noon'] < 7 || amount['noon'] > 18"
                :pictureUrl="getService('dinerlasalleovale').Photo"
                :isSelected="selectedDisposition === 'dinerlasalleovale'"
                :hasQuantity="false"
                v-model="amount['noon']"
                @click="selectDisposition('dinerlasalleovale')"
              />
            </div>
          </span>
        </div>
      </div>

      <div class="bpa-front-dc--footer">
        <div class="el-row"><div class="el-col el-col-24">
          <div class="bpa-front-tabs--foot footer-eauget">
            <subtotal />
            <button type="button" @click="$emit('nextStep')"
              :disabled="!canGoNext()" :class="{ 'is-disabled': !canGoNext() }"
              class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default eauget-next-step"
              data-testid="btn-next">
              <span>Suivant :&nbsp;<strong>Menu</strong>
                <svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" viewBox="0 0 24 24">
                  <rect fill="none" height="24" width="24"></rect>
                  <path d="M14.29,5.71L14.29,5.71c-0.39,0.39-0.39,1.02,0,1.41L18.17,11H3c-0.55,0-1,0.45-1,1v0c0,0.55,0.45,1,1,1h15.18l-3.88,3.88c-0.39,0.39-0.39,1.02,0,1.41l0,0c0.39,0.39,1.02,0.39,1.41,0l5.59-5.59c0.39-0.39,0.39-1.02,0-1.41L15.7,5.71C15.32,5.32,14.68,5.32,14.29,5.71z"></path>
                </svg>
              </span>
            </button>
          </div>
        </div></div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
